import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, AbstractControl, Validators } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonToggleModule
  ],
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss']
})
export class FilterDialogComponent implements OnInit {
  queryForm!: FormGroup;

  fields = [
    { value: 'code', viewValue: 'Code' },
    { value: 'name', viewValue: 'Name' },
    { value: 'category', viewValue: 'Category' },
    { value: 'price', viewValue: 'Price' }
  ];

  operators = [
    { value: 'Is equal to', viewValue: 'Is equal to' },
    { value: 'Is not equal to', viewValue: 'Is not equal to' },
    { value: 'In', viewValue: 'In' },
    { value: 'not_in', viewValue: 'Not In' },
    { value: 'contains', viewValue: 'Contains' },
    { value: 'greater_than', viewValue: 'Greater than' },
    { value: 'less_than', viewValue: 'Less than' }
  ];

  constructor(
    public dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    if (Array.isArray(this.data?.tableColumns) && this.data.tableColumns.length > 0) {
      this.fields = this.data.tableColumns
        .filter((col: any) => col && col.key)
        .map((col: any) => ({
          value: col.key,
          viewValue: col.label || this.toTitleCase(String(col.key))
        }));
    }
    this.queryForm = this.createRuleSetGroup();
  }

  private toTitleCase(str: string): string {
    const withSpaces = str.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, ' $2');
    return withSpaces.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  }

  createRuleSetGroup(): FormGroup {
    return this.fb.group({
      condition: ['AND'],
      rules: this.fb.array([])
    });
  }

  createRuleGroup(): FormGroup {
    return this.fb.group({
      field: ['', Validators.required],
      operator: ['', Validators.required],
      value: ['', Validators.required]
    });
  }

  getRules(formGroup: AbstractControl): FormArray {
    return formGroup.get('rules') as FormArray;
  }

  addRule(formGroup: AbstractControl): void {
    this.getRules(formGroup).push(this.createRuleGroup());
  }

  addRuleSet(formGroup: AbstractControl): void {
    this.getRules(formGroup).push(this.createRuleSetGroup());
  }

  removeRule(formGroup: AbstractControl, index: number): void {
    this.getRules(formGroup).removeAt(index);
  }

  isRuleSet(control: AbstractControl): boolean {
    return !!control.get('rules');
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onApply(): void {
    if (this.queryForm.valid) {
      const formattedQuery = this.formatQuery(this.queryForm);
      console.log('Formatted Query:', formattedQuery);
      this.dialogRef.close(formattedQuery);
    } else {
      this.queryForm.markAllAsTouched();
    }
  }

  private formatQuery(group: AbstractControl): { filters: any[] } {
    const formValue = group.getRawValue();
    return {
      filters: this.transformRules(formValue.rules, formValue.condition)
    };
  }

  private transformRules(rules: any[], parentCondition: string): any[] {
    if (!rules) {
      return [];
    }
    return rules.map(rule => {
      if (rule.rules) {
        // This is a rule set (group)
        return {
          filterType: 'CONDITIONGROUP',
          joinType: parentCondition,
          value: '',
          filters: this.transformRules(rule.rules, rule.condition),
           dataType: 'string'
        };
      } else {
        // This is a single rule (condition)
        return {
          filterType: 'CONDITION',
          joinType: parentCondition,
          operatorType: this.mapOperator(rule.operator),
          key: rule.field,
          value: rule.value,
          dataType: 'string' // Assuming string for now
        };
      }
    });
  }

  private mapOperator(op: string): string {
    const mapping: { [key: string]: string } = {
      'equal': 'EQUALS',
      'not_equal': 'IS_NOT_EQUAL',
      'in': 'IN',
      'not_in': 'NOT_IN',
      'contains': 'CONTAINS',
      'greater_than': 'GREATER_THAN',
      'less_than': 'LESS_THAN'
    };
    return mapping[op] || op.toUpperCase();
  }
}
